import { cn } from '@/app/utils';

interface ProgressiveBlurProps {
  /**
   * 模糊方向
   * @default 'to right'
   */
  direction?: 'to right' | 'to left' | 'to top' | 'to bottom';

  /**
   * 模糊强度配置 [start, step1, step2, end]
   * @default [1, 3, 6, 10]
   */
  blurIntensity?: [number, number, number, number];

  /**
   * 自定义 className
   */
  className?: string;

  /**
   * 是否启用圆角
   * @default true
   */
  rounded?: boolean;

  /**
   * 自定义圆角大小
   * @default 'rounded-8'
   */
  roundedSize?: string;

  /**
   * 渐变遮罩的关键点位置 [start, point1, point2, point3, end]
   * @default [0, 30, 60, 85, 100]
   */
  gradientStops?: [number, number, number, number, number];

  /**
   * 渐变遮罩的不透明度配置
   * @default [0, 0.2, 0.5, 0.8, 1]
   */
  gradientOpacity?: [number, number, number, number, number];
}

/**
 * 渐进式模糊组件 - 模拟 Figma 的 Progressive Blur 效果
 *
 * @example
 * // 基础用法：从左到右模糊
 * <ProgressiveBlur />
 *
 * // 自定义方向和强度：从右到左模糊
 * <ProgressiveBlur
 *   direction="to left"
 *   blurIntensity={[0, 2, 5, 8]}
 * />
 *
 * // 垂直方向模糊：从上到下模糊
 * <ProgressiveBlur
 *   direction="to bottom"
 *   gradientStops={[0, 20, 40, 70, 100]}
 * />
 */
export function ProgressiveBlur({
  direction = 'to right',
  blurIntensity = [1, 3, 6, 10],
  className,
  rounded = true,
  roundedSize = 'rounded-8',
  gradientStops = [0, 30, 60, 85, 100],
  gradientOpacity = [0, 0.2, 0.5, 0.8, 1],
}: ProgressiveBlurProps) {
  const baseClassName = cn('absolute inset-0', rounded && roundedSize, className);

  // 生成渐变遮罩字符串
  const createMask = (startPercent: number, opacity: number[]) => {
    const stops = [
      `transparent ${startPercent}%`,
      startPercent < gradientStops[1] ? `rgba(0,0,0,${opacity[1]}) ${gradientStops[1]}%` : null,
      startPercent < gradientStops[2] ? `rgba(0,0,0,${opacity[2]}) ${gradientStops[2]}%` : null,
      startPercent < gradientStops[3] ? `rgba(0,0,0,${opacity[3]}) ${gradientStops[3]}%` : null,
      `black ${gradientStops[4]}%`,
    ]
      .filter(Boolean)
      .join(', ');

    return `linear-gradient(${direction}, ${stops})`;
  };

  return (
    <>
      {/* 基础层：整体轻微模糊 */}
      <div
        className={cn(baseClassName, 'backdrop-blur-[var(--blur-0)]')}
        style={
          {
            '--blur-0': `${blurIntensity[0]}px`,
          } as React.CSSProperties
        }
      />

      {/* 渐进层：使用更精细的遮罩模拟渐进效果 */}
      <div
        className={cn(baseClassName, 'backdrop-blur-[var(--blur-1)]')}
        style={
          {
            '--blur-1': `${blurIntensity[1]}px`,
            mask: createMask(gradientStops[0], gradientOpacity),
            WebkitMask: createMask(gradientStops[0], gradientOpacity),
          } as React.CSSProperties
        }
      />

      {/* 强化层：更强的模糊 */}
      <div
        className={cn(baseClassName, 'backdrop-blur-[var(--blur-2)]')}
        style={
          {
            '--blur-2': `${blurIntensity[2]}px`,
            mask: createMask(gradientStops[1], gradientOpacity),
            WebkitMask: createMask(gradientStops[1], gradientOpacity),
          } as React.CSSProperties
        }
      />

      {/* 最终层：最强模糊 */}
      <div
        className={cn(baseClassName, 'backdrop-blur-[var(--blur-3)]')}
        style={
          {
            '--blur-3': `${blurIntensity[3]}px`,
            mask: createMask(gradientStops[2], gradientOpacity),
            WebkitMask: createMask(gradientStops[2], gradientOpacity),
          } as React.CSSProperties
        }
      />
    </>
  );
}
